import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Assignment as BorrowIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Inventory as InventoryIcon,
  Numbers as NumbersIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory } from '../../../utils/inventoryPermissions';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import {
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
  applyApiValidationErrors,
} from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { SUCCESS } from '../../common/SaaSTokens';
import TextField from '@mui/material/TextField';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx, pickerFieldSx } from '../../common/formFieldSx';

const BORROW_VALIDATION_FIELDS = new Set([
  'borrower_name', 'borrower_contact', 'stock_id', 'quantity',
  'penalty_date', 'return_date', 'status', 'penalty', 'remark',
]);

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

// ─── Base CSS (animations + autofill) ─────────────────────────────────────────
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }

  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

// ─── Picker CSS — unified dark calendar popup theme ────────────────────────────
const PICKER_CSS = `
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersDay-root.Mui-disabled,
  .MuiPickersDay-root[aria-disabled="true"] {
    color: rgba(100, 116, 139, 0.45) !important;
    -webkit-text-fill-color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    opacity: 1 !important;
    text-decoration: none !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
  }
  .MuiPickersDay-root.Mui-disabled:hover,
  .MuiPickersDay-root[aria-disabled="true"]:hover {
    color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    box-shadow: none !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersLayout-toolbar,
  .MuiDatePickerToolbar-root {
    background: rgba(5, 13, 26, 0.96) !important;
    border-bottom: 1px solid rgba(56,189,248,0.15) !important;
  }
  .MuiPickersToolbarText-root,
  .MuiDatePickerToolbar-title {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
  }
  .MuiPickersActionBar-root {
    border-top: 1px solid rgba(56,189,248,0.12) !important;
    background: rgba(5, 13, 26, 0.9) !important;
  }
  .MuiPickersActionBar-root .MuiButton-root {
    color: rgba(56,189,248,0.9) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 600 !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
  .MuiMonthCalendar-root .Mui-disabled,
  .MuiYearCalendar-root .Mui-disabled {
    color: rgba(100, 116, 139, 0.45) !important;
    opacity: 1 !important;
    background: transparent !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root,
  .MuiPickersOutlinedInput-root {
    border-radius: 10px !important;
    background: rgba(15, 31, 56, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    transition: box-shadow 0.2s ease, border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.18) !important;
    transition: border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersTextField-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.4) !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.65) !important;
    border-width: 1.5px !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused,
  .MuiPickersOutlinedInput-root.Mui-focused {
    box-shadow: 0 0 0 3px rgba(56,189,248,0.12), 0 0 14px rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.875rem !important;
    color: rgba(56,189,248,0.45) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root.Mui-focused {
    color: rgba(56,189,248,0.85) !important;
    font-weight: 600 !important;
  }
  .MuiPickersTextField-root .MuiFormLabel-asterisk {
    color: #EF4444 !important;
    font-weight: 700 !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionStart .MuiSvgIcon-root {
    color: rgba(56,189,248,0.45) !important;
    font-size: 18px !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root {
    color: rgba(56,189,248,0.35) !important;
    padding: 6px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root:hover {
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiSvgIcon-root {
    font-size: 17px !important;
  }
  .MuiPickersSectionList-root {
    color: rgba(226,232,240,0.85) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.9rem !important;
  }
  .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-separator {
    color: rgba(226,232,240,0.85) !important;
    -webkit-text-fill-color: rgba(226,232,240,0.85) !important;
  }
  .MuiPickersSectionList-sectionContent[data-value=""] {
    color: rgba(56,189,248,0.3) !important;
    -webkit-text-fill-color: rgba(56,189,248,0.3) !important;
  }
  .MuiPickersSectionList-section:focus .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-sectionContent:focus {
    background: rgba(56,189,248,0.15) !important;
    border-radius: 3px !important;
    color: #38BDF8 !important;
    -webkit-text-fill-color: #38BDF8 !important;
    outline: none !important;
  }
`;

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#94A3B8';

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── StyledField ──────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

// ─── DatePicker slot factory ───────────────────────────────────────────────────
const makePickerSlots = (size = 'normal', icon = null) => (disabled = false) => ({
  textField: {
    fullWidth: true,
    size,
    InputLabelProps: { shrink: true },
    InputProps: icon
      ? {
          startAdornment: (
            <InputAdornment position="start">
              {React.cloneElement(icon, { sx: { fontSize: 18 } })}
            </InputAdornment>
          ),
        }
      : undefined,
    sx: pickerFieldSx(size, { disabled }),
  },
  popper: { placement: 'bottom-start' },
});

const penaltyPickerSlots = makePickerSlots('normal', <CalendarTodayIcon />)(false);

/** Auto-overdue from penalty date only when not returned; never infer returned from return_date alone. */
const resolveBorrowStatus = (status, penaltyDate) => {
  if (status === 'returned') return status;
  if (penaltyDate && dayjs(penaltyDate).isBefore(dayjs(), 'day')) return 'overdue';
  return status;
};

// ─── Main Component ────────────────────────────────────────────────────────────
const BorrowForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_contact: '',
    penalty_date: null,
    return_date: null,
    stock_id: '',
    quantity: '',
    penalty: 0.00,
    remark: '',
    status: 'pending',
  });

  const [stocks,                  setStocks]                  = useState([]);
  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(true);
  const [error,                   setError]                   = useState(null);
  const [successMessage,          setSuccessMessage]          = useState('');
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [originalBorrow,          setOriginalBorrow]          = useState(null);
  const [penaltyDateOpen,         setPenaltyDateOpen]         = useState(false);
  const [returnDateOpen,          setReturnDateOpen]          = useState(false);

  const canManageBorrow = canManageInventory(currentUser);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'borrow',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  const statusOptions = [
    { value: 'pending',  label: 'Pending'  },
    { value: 'approved', label: 'Approved' },
    { value: 'returned', label: 'Returned' },
    { value: 'overdue',  label: 'Overdue'  },
  ];

  // ── Inject CSS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const cssId = 'borrowform-ds-css';
    let el = document.getElementById(cssId);
    if (!el) { el = document.createElement('style'); el.id = cssId; document.head.appendChild(el); }
    el.innerHTML = FORM_CSS + PICKER_CSS;
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!canManageBorrow) { navigate('/dashboard'); return; }
    fetchStocks();
    if (isEditMode) fetchBorrowRecord();
    else setDataLoading(false);
  }, [currentUser, id, isEditMode, canManageBorrow]);

  useEffect(() => {
    if (formData.status !== 'returned') setReturnDateOpen(false);
  }, [formData.status]);

  const fetchStocks = async () => {
    try {
      const response = await api.get('/stocks');
      setStocks(response.data);
    } catch { setError('Failed to fetch stock items. Please try again.'); }
  };

  const fetchBorrowRecord = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/borrows/${id}`);
      const borrow = response.data;
      setOriginalBorrow({
        stock_id: borrow.stock_id || null,
        quantity: Number(borrow.quantity) || 0,
        status: borrow.status || 'pending',
      });
      const penaltyDay = borrow.penalty_date ? dayjs(borrow.penalty_date) : null;
      const returnDay = borrow.return_date ? dayjs(borrow.return_date) : null;
      const status = resolveBorrowStatus(borrow.status || 'pending', penaltyDay);
      setFormData({
        borrower_name:    borrow.borrower_name    || '',
        borrower_contact: borrow.borrower_contact || '',
        penalty_date:     penaltyDay,
        return_date:      returnDay,
        stock_id:         borrow.stock_id  || '',
        quantity:         borrow.quantity  || '',
        penalty:          borrow.penalty   || '',
        remark:           borrow.remark    || '',
        status,
      });
    } catch { setError('Failed to load borrow record. Please try again.'); navigate('/borrows'); }
    finally   { setLoading(false); setDataLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePenaltyDateChange = (date) => {
    setFormData((prev) => {
      const next = { ...prev, penalty_date: date };
      if (next.status !== 'returned') {
        next.status = resolveBorrowStatus(next.status, date);
      }
      return next;
    });
    if (error) setError(null);
    if (showFieldErrors && validationErrors.penalty_date) {
      setValidationErrors(prev => ({ ...prev, penalty_date: '' }));
    }
  };

  const handleReturnDateChange = (date) => {
    setFormData((prev) => ({ ...prev, return_date: date, status: 'returned' }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors.return_date) {
      setValidationErrors(prev => ({ ...prev, return_date: '' }));
    }
  };

  const handleStatusChange = (status) => {
    setFormData((prev) => {
      if (status === 'returned') {
        return {
          ...prev,
          status: 'returned',
          return_date: prev.return_date || dayjs(),
        };
      }
      const next = {
        ...prev,
        status: resolveBorrowStatus(status, prev.penalty_date),
        return_date: null,
      };
      return next;
    });
  };

  const buildSubmitData = () => {
    const status = resolveBorrowStatus(formData.status, formData.penalty_date);
    const payload = {
      ...formData,
      status,
      penalty_date: formData.penalty_date ? formData.penalty_date.format('YYYY-MM-DD') : null,
      return_date:
        status === 'returned'
          ? (formData.return_date || dayjs()).format('YYYY-MM-DD')
          : null,
      quantity: parseInt(formData.quantity, 10) || 0,
      penalty: formData.penalty === '' ? 0 : parseFloat(formData.penalty),
      stock_id: formData.stock_id || null,
      remark: formData.remark?.trim() || null,
    };
    return payload;
  };

  const collectValidationErrors = () => {
    const errors = {};
    const selectedStock = getSelectedStock();
    const availableForSelection = getEffectiveAvailableQuantity(selectedStock);
    const qty = Number.parseInt(String(formData.quantity ?? '').trim(), 10);

    if (!formData.borrower_name?.trim()) errors.borrower_name = 'Borrower name is required';
    if (!formData.borrower_contact?.trim()) errors.borrower_contact = 'Contact information is required';
    else if (formData.borrower_contact.trim().length < 5) errors.borrower_contact = 'Invalid phone number';
    if (!formData.stock_id) errors.stock_id = 'Please select an item to borrow';

    if (String(formData.quantity ?? '').trim() === '') errors.quantity = 'Quantity is required';
    else if (Number.isNaN(qty) || qty <= 0) errors.quantity = 'Quantity must be at least 1';
    else if (selectedStock && qty > availableForSelection) errors.quantity = `Only ${availableForSelection} items available`;

    if (!formData.penalty_date) errors.penalty_date = 'Penalty date is required';
    else if (!isEditMode && formData.penalty_date.isBefore(dayjs(), 'day')) errors.penalty_date = 'Penalty date cannot be in the past';

    if (formData.status === 'returned' && !formData.return_date) {
      errors.return_date = 'Return date is required when status is Returned';
    }

    return errors;
  };

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true);
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setShowFieldErrors(false);
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, BORROW_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!BORROW_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      const data = buildSubmitData();
      if (isEditMode) await api.put(`/borrows/${id}`, data);
      else await api.post('/borrows', data);
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage(`Borrow record ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/borrows', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      handleApiError(err, `Failed to ${isEditMode ? 'update' : 'save'} borrow record. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditMode) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmUpdateDialogOpen(true);
      return;
    }
    setError(null);
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true);
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
      });
      return;
    }
    setConfirmUpdateDialogOpen(false);
    await doSave();
  };

  const getSelectedStock = () => stocks.find(s => s.id === parseInt(formData.stock_id));
  const getEffectiveAvailableQuantity = (stock) => {
    if (!stock) return 0;
    const baseAvailable = Number(stock.available_quantity) || 0;
    if (!isEditMode || !originalBorrow) return baseAvailable;
    if (Number(stock.id) === Number(originalBorrow.stock_id)) {
      return baseAvailable + (Number(originalBorrow.quantity) || 0);
    }
    return baseAvailable;
  };
  const getDaysUntilPenalty = () => (
    formData.penalty_date ? formData.penalty_date.startOf('day').diff(dayjs().startOf('day'), 'day') : null
  );
  const isReturned = formData.status === 'returned';
  const canEditReturnDate = isReturned;
  const returnPickerDisabled = !canEditReturnDate;
  const returnPickerSlots = makePickerSlots('normal', <CalendarTodayIcon />)(returnPickerDisabled);

  // ── FIX: stockOptions blank entry now has a label ──────────────────────────
  const stockOptions = [
    ...stocks
      .filter((stock) => (
        !stock.archived_at
        && (
          Number(stock.available_quantity) > 0
          || Number(stock.id) === Number(originalBorrow?.stock_id)
        )
      ))
      .map((stock) => ({
      value: String(stock.id),
      label: `${stock.things} (Available: ${getEffectiveAvailableQuantity(stock)})`,
    })),
  ];

  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message={isEditMode ? 'Loading borrow record' : 'Loading borrow form'} size={56} variant="primary" />
    </Box>
  );
  if (!currentUser || !canManageBorrow) return null;

  const canSubmit = Boolean(
    formData.borrower_name.trim() &&
    formData.borrower_contact.trim() &&
    formData.stock_id &&
    String(formData.quantity ?? '').trim() !== '' &&
    formData.penalty_date
  );

  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

        <Header
          title={isEditMode ? 'Edit Borrow Request' : 'Create New Borrow Request'}
          subtitle={isEditMode ? 'Update borrow request details' : 'Create a new equipment borrowing request'}
          icon={BorrowIcon}
          showBackButton
          onBackClick={() => navigate(-1)}
          backButtonText="Back to Borrows"
        />

        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
        />
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
        />

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Borrower Information ─────────────────────────────────────── */}
            <SectionCard title="Borrower Information" icon={<PersonIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <StyledField
                  fullWidth required label="Borrower Name" name="borrower_name" value={formData.borrower_name}
                  onChange={handleChange}
                  error={fieldHasError('borrower_name')}
                  helperText={fieldHelper('borrower_name')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />
                <StyledField
                  fullWidth required label="Contact Information" name="borrower_contact" value={formData.borrower_contact}
                  onChange={handleChange}
                  error={fieldHasError('borrower_contact')}
                  helperText={fieldHelper('borrower_contact')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />
              </Box>
            </SectionCard>

            {/* ── Item & Quantity ──────────────────────────────────────────── */}
            <SectionCard title="Item & Quantity" icon={<InventoryIcon />} delay={0.06}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5}}>
                  <SearchableSelect
                    required
                    label="Inventory Item"
                    value={formData.stock_id ? String(formData.stock_id) : ''}
                    onChange={(v) => handleFieldChange('stock_id', v)}
                    options={stockOptions}
                    error={fieldHasError('stock_id')}
                    helperText={fieldHelper('stock_id')}
                    startIcon={<InventoryIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  />

                  <StyledField
                    fullWidth required label="Quantity" name="quantity" type="number" value={formData.quantity}
                    onChange={handleChange}
                    error={fieldHasError('quantity')}
                    helperText={fieldHelper('quantity')}
                    inputProps={{ max: getEffectiveAvailableQuantity(getSelectedStock()) || 999 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>

                {getSelectedStock() && (
                  <Box sx={{ p: 2.5, borderRadius: '10px', background: alpha(NEON, 0.05), border: `1px solid ${alpha(NEON, 0.2)}`, display: 'flex', gap: 2 }}>
                    <InfoIcon sx={{ color: alpha(NEON, 0.7), flexShrink: 0, mt: 0.3 }} />
                    <Box>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(TEXT_BODY, 0.85), mb: 0.5 }}>
                        <strong>Item:</strong> {getSelectedStock().things}
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(TEXT_BODY, 0.75) }}>
                        <strong>Available:</strong> {getEffectiveAvailableQuantity(getSelectedStock())} | <strong>Location:</strong> {getSelectedStock().place}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </SectionCard>

            {/* ── Penalty date, return date & status ─────────────────────── */}
            <SectionCard title="Dates & Status" icon={<ScheduleIcon />} delay={0.12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <SearchableSelect
                  label="Status"
                  value={formData.status}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  disabled={Boolean(originalBorrow?.archived_at)}
                  helperText={originalBorrow?.archived_at
                    ? 'Archived workflow status is locked; other details can still be corrected.'
                    : undefined}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, width: '100%' }}>
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <DatePicker
                      label="Penalty Date"
                      value={formData.penalty_date}
                      open={penaltyDateOpen}
                      onOpen={() => setPenaltyDateOpen(true)}
                      onClose={() => setPenaltyDateOpen(false)}
                      onChange={handlePenaltyDateChange}
                      minDate={isEditMode ? undefined : dayjs()}
                      slotProps={{
                        ...penaltyPickerSlots,
                        textField: {
                          ...penaltyPickerSlots.textField,
                          required: true,
                          error: fieldHasError('penalty_date'),
                          helperText: fieldHelper('penalty_date'),
                          onClick: () => setPenaltyDateOpen(true),
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPenaltyDateOpen(true);
                            }
                          },
                        },
                        openPickerButton: {
                          onClick: (e) => { e.stopPropagation(); setPenaltyDateOpen(true); },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, width: '100%' }}>
                    <DatePicker
                      key={`return-${formData.status}`}
                      label="Return Date"
                      value={formData.return_date ?? null}
                      maxDate={dayjs()}
                      open={returnDateOpen}
                      onOpen={() => canEditReturnDate && setReturnDateOpen(true)}
                      onClose={() => setReturnDateOpen(false)}
                      onChange={handleReturnDateChange}
                      disabled={returnPickerDisabled}
                      slotProps={(() => {
                        const openPicker = () => {
                          if (!returnPickerDisabled) setReturnDateOpen(true);
                        };
                        return {
                          ...returnPickerSlots,
                          textField: {
                            ...returnPickerSlots.textField,
                            required: isReturned,
                            error: fieldHasError('return_date'),
                            helperText: fieldHelper('return_date'),
                            onClick: openPicker,
                            onKeyDown: (e) => {
                              if (returnPickerDisabled) return;
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); }
                            },
                          },
                          openPickerButton: {
                            onClick: (e) => { e.stopPropagation(); openPicker(); },
                          },
                        };
                      })()}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    background: alpha(NEON, 0.04),
                    border: `1px solid ${alpha(NEON, 0.14)}`,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                  }}
                >
                  <InfoIcon sx={{ color: alpha(NEON, 0.55), flexShrink: 0, mt: 0.15, fontSize: 18 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.82), lineHeight: 1.45 }}>
                      Set status to <Box component="span" sx={{ color: NEON, fontWeight: 600 }}>Returned</Box> to unlock return date.
                    </Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.72), lineHeight: 1.45 }}>
                      Return date defaults to today when status changes to Returned; adjust it if the item was returned on another day.
                    </Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.72), lineHeight: 1.45 }}>
                      Past penalty dates mark the request as <Box component="span" sx={{ color: NEON2, fontWeight: 600 }}>Overdue</Box> unless status is Returned.
                    </Typography>
                    {canEditReturnDate && formData.return_date && (
                      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: alpha(NEON, 0.65), letterSpacing: 0.2, mt: 0.25 }}>
                        Selected: {dayjs(formData.return_date).format('dddd, D MMMM YYYY')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <StyledField
                  fullWidth
                  label="Penalty Amount"
                  name="penalty"
                  value={formData.penalty}
                  onChange={handleChange}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><MoneyIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />

                <StyledField
                  fullWidth
                  label="Remark"
                  name="remark"
                  value={formData.remark}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {formData.penalty_date && getDaysUntilPenalty() !== null && !isReturned && (
                  <Box sx={{ p: 2, borderRadius: '10px', background: alpha(NEON2, 0.05), border: `1px solid ${alpha(NEON2, 0.2)}`, display: 'flex', gap: 1.5 }}>
                    <TimeIcon sx={{ color: alpha(NEON2, 0.7), flexShrink: 0, mt: 0.3 }} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(TEXT_BODY, 0.85) }}>
                      {getDaysUntilPenalty() < 0 ? (
                        <><strong>Overdue by:</strong> {Math.abs(getDaysUntilPenalty())} day(s)</>
                      ) : (
                        <><strong>Days until penalty date:</strong> {getDaysUntilPenalty()} day(s)</>
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </SectionCard>

            {/* ── Submit ──────────────────────────────────────────────────── */}
            <SubmitActionsCard
              statusText={isEditMode ? 'Save changes to update this borrow request.' : 'Complete the form to create a new borrow request.'}
              missingRequiredFields={isEditMode ? null : [!formData.borrower_name.trim() && 'Borrower Name', !formData.borrower_contact.trim() && 'Contact Information', !formData.stock_id && 'Inventory Item', String(formData.quantity ?? '').trim() === '' && 'Quantity', !formData.penalty_date && 'Penalty Date'].filter(Boolean)}
              readyStatusText="All required fields are complete. Ready to create borrow request."
              cancelLabel="Cancel"
              onCancel={() => navigate(-1)}
              cancelDisabled={loading}
              submitLabel={isEditMode ? 'Update Request' : 'Create Request'}
              submitDisabled={loading || !canSubmit}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              showSubmit
              submitType="submit"
              {...submitDraftProps}
            />

          </Box>
        </form>

        <ConfirmUpdateDialog
          open={confirmUpdateDialogOpen}
          onClose={() => setConfirmUpdateDialogOpen(false)}
          onConfirm={handleConfirmUpdate}
          variant="edit"
          itemType="borrow record"
          itemName={formData.borrower_name}
          title="Confirm Update"
          description="Are you sure you want to update this borrow request?"
          confirmLabel="Update Request"
          loading={loading}
          confirmDisabled={!canSubmit}
        />

      </Container>
    </LocalizationProvider>
  );
};

export default BorrowForm;
